import type * as z from "zod";
import type { connectStepToApp as connectStepToAppTool } from "../../api/ai/tools/connectToApp";
import { useZero } from "@rocicorp/zero/react";
import { useEffect } from "react";
import { findConnectorTypes } from "../../shared/connectors/findConnectorTypes";
import { getConnectorAiContext } from "../../shared/connectors/getAiContext";
import { sharedMutators } from "../../shared/mutators";
import type {
  ChatTool,
  PendingPromptArgs,
  ToolUseProps,
} from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { Text } from "../components/Text";
import { useGetPresence } from "../hooks/useGetPresence";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import { useNavigateOnBranchChange } from "../hooks/useNavigateOnBranchChange";
import { maybeDisplayBrowserNotification } from "../lib/browserNotifications";
import { otherCollaboratorsPresent } from "../lib/connectPrompt";
import { type SelectableConnector } from "../hooks/useConnectorSelectOnSave";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { ConnectorPrompt, mutationErrorMessage } from "./ConnectorPrompt";
import { ConnectedConnectorStatus } from "./ConnectedConnectorStatus";

export type ConnectToAppArgs =
  | { branchId: string; spaceId?: string; personalChatId?: never }
  | { personalChatId: string; chatOwnerId: string; branchId?: never };

type Input = Partial<
  z.infer<ReturnType<typeof connectStepToAppTool>["inputSchema"]>
>;
type Output =
  | {
      connected: true;
      connectorId?: string;
      connectedAppName: string;
      appIdentifier?: string;
    }
  | { dismissed: true }
  | { noAppsAvailable: true; error: string }
  | { error: string };

type ResultHandler = (
  output: Output & { step?: string; aiContext?: string },
  isError: boolean,
  options?: { pauseAfter?: boolean }
) => void;

export function connectedOutput(app: SelectableConnector, step?: string) {
  const aiContext = getConnectorAiContext(app);
  return {
    connected: true as const,
    connectorId: app.id,
    connectedAppName: app.name,
    appIdentifier: app.identifier,
    ...(step && { step }),
    ...(aiContext && { aiContext }),
  };
}

function isPersonalConnectArgs(
  args: ConnectToAppArgs
): args is Extract<ConnectToAppArgs, { personalChatId: string }> {
  return typeof args.personalChatId === "string";
}

function outputKind(
  output: Output
): "connected" | "dismissed" | "noAppsAvailable" | "error" {
  if ("noAppsAvailable" in output) return "noAppsAvailable";
  if ("connected" in output) return "connected";
  if ("dismissed" in output) return "dismissed";
  return "error";
}

function ConnectToAppHeader({
  toolUse,
  result,
  interrupted,
  data,
}: ToolUseProps) {
  const input = toolUse.input as Input | undefined;
  const output = result?.output as Output | undefined;
  const stepName = input?.step;
  const targetLabel = input?.targetUrl ?? input?.search ?? "connector";

  if (!output) {
    return (
      <ToolStatus
        icon="circle-dashed"
        text={`Asking to connect ${targetLabel}…`}
        pending={!interrupted}
      />
    );
  }

  switch (outputKind(output)) {
    case "noAppsAvailable":
      return (
        <ToolStatus
          icon="check-empty"
          text={`No matching connectors available for ${targetLabel}`}
        />
      );
    case "error":
      return (
        <ToolStatus
          icon="circle-off"
          iconColor="var(--red-600)"
          text={`Failed to connect ${targetLabel}`}
        />
      );
    case "dismissed":
      return (
        <ToolStatus
          icon="check-empty"
          text={`Cancelled connecting ${targetLabel}`}
        />
      );
    case "connected": {
      const connected = output as Extract<Output, { connected: true }>;
      const scope = data as
        | { spaceId?: string; chatOwnerId?: string }
        | undefined;
      return (
        <ConnectedConnectorStatus
          connectorId={
            connected.connectorId ??
            (scope?.spaceId ? input?.connectorId : undefined)
          }
          step={stepName}
          spaceId={scope?.spaceId}
          chatOwnerId={scope?.chatOwnerId}
        />
      );
    }
  }
}

function ConnectToAppBody({ result }: ToolUseProps) {
  const output = result?.output as Output | undefined;
  if (!output) return null;
  if ("error" in output) {
    return (
      <Text variant="text-small" color="var(--text-secondary)">
        {output.error}
      </Text>
    );
  }
  return null;
}

function useAssociateStepWithApp(
  workflowBranchId: string,
  onResult: ResultHandler
) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(workflowBranchId);
  const {
    spaceId,
    workflowId,
    step: urlStep,
  } = useCurrentUrl(routes.space.workflow);
  return async (app: SelectableConnector, stepDir: string) => {
    let serverPromise: Promise<{ type: string; error?: unknown }>;
    const forkBranchId = await navigateOnBranchChange((branchParams) => {
      const result = z.mutate(
        sharedMutators.associateStepWithConnectedApp({
          ...branchParams,
          stepDir,
          connectedAppId: app.id,
          silent: true,
        })
      );
      serverPromise = result.server;
      return result;
    });
    const serverResult = await serverPromise!;
    if (serverResult.type === "error") {
      onResult({ error: mutationErrorMessage(serverResult.error) }, true);
      return;
    }
    if (!forkBranchId) {
      navigate(
        routes.space.workflow,
        { spaceId, workflowId, step: urlStep },
        { replace: true }
      );
    }
    onResult(connectedOutput(app, stepDir), false);
  };
}

export function useConnectNotification(
  { step, targetUrl, search }: Pick<Input, "step" | "targetUrl" | "search">,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;
    const target = targetUrl ?? search;
    maybeDisplayBrowserNotification(
      "3B needs you",
      target
        ? `Connect to ${target}`
        : step
          ? `Connect a connector to ${step}`
          : "Connect a connector"
    );
  }, [enabled, targetUrl, search, step]);
}

function PendingConnectToAppPrompt({
  args,
  pending,
  onResult,
}: {
  args: ConnectToAppArgs;
  pending: Input & { toolCallId: string };
  onResult: ResultHandler;
}) {
  const appIdentifiers = findConnectorTypes({
    search: pending.search,
    targetUrl: pending.targetUrl,
  }).map((app) => app.identifier);
  useConnectNotification(pending);

  const z = useZero();
  const personal = isPersonalConnectArgs(args);
  const personalChatId = personal ? args.personalChatId : "";
  const workflowBranchId = personal ? "" : args.branchId;
  const { spaceId } = useCurrentUrl(routes.space.workflow);
  const presence = useGetPresence();
  const currentUserId = useCurrentUserId();
  const othersPresent = otherCollaboratorsPresent({
    personal,
    currentUserId,
    presentUserIds: presence.presentByBranch.get(workflowBranchId) ?? [],
  });

  const associateStep = useAssociateStepWithApp(workflowBranchId, onResult);
  const handleSelectApp = async (app: SelectableConnector) => {
    if (!personal && pending.step) {
      await associateStep(app, pending.step);
      return;
    }
    // Personal chats record an attachment; build chats record the
    // workflow-wide chat confirmation — picking here is the confirmation
    // moment, so connectors created or authorized through this picker
    // never re-prompt.
    const result = z.mutate(
      personal
        ? sharedMutators.associatePersonalChatWithConnectedApp({
            personalChatId,
            connectedAppId: app.id,
          })
        : sharedMutators.associateBranchChatWithConnectedApp({
            branchId: workflowBranchId,
            connectedAppId: app.id,
          })
    );
    const serverResult = await result.server;
    if (serverResult.type === "error") {
      onResult({ error: mutationErrorMessage(serverResult.error) }, true);
      return;
    }
    onResult(connectedOutput(app), false);
  };

  return (
    <ConnectorPrompt
      pending={{ ...pending, appIdentifiers }}
      othersPresent={othersPresent}
      spaceId={personal ? undefined : spaceId}
      openCreatedApp={(connectedAppId) => {
        navigate(routes.modal.connector, { id: connectedAppId });
      }}
      onSelectApp={handleSelectApp}
      onDismiss={() => onResult({ dismissed: true }, false)}
      onError={(message) => onResult({ error: message }, true)}
      onNoApps={
        personal
          ? () =>
              onResult(
                {
                  noAppsAvailable: true,
                  error:
                    "No connectors match this request and the user does not have permission to create new ones. Tell the user they need to ask a tenant admin to create or grant access to a connector.",
                },
                false
              )
          : undefined
      }
      onTypeInstead={() =>
        onResult({ dismissed: true }, false, { pauseAfter: true })
      }
    />
  );
}

export const connectToAppActivity = (
  name: string,
  spaceId?: string
): ChatTool => ({
  name,
  data: { spaceId },
  Header: ConnectToAppHeader,
  Body: ConnectToAppBody,
  alwaysLoadOutput: true,
});

function ConnectToAppPendingPrompt({
  toolCallId,
  input,
  onResult,
  data,
}: PendingPromptArgs) {
  const args = data as ConnectToAppArgs;
  const parsed = input as Input | null;
  if (!parsed) return null;
  // A connectorId renders the same connector prompt narrowed to that one
  // connector — picking it is the confirmation, whether it attaches to a
  // step or to the chat.
  const connectorId = isPersonalConnectArgs(args)
    ? undefined
    : parsed.connectorId;
  if (!connectorId && !parsed.targetUrl && !parsed.search) return null;

  return (
    <PendingConnectToAppPrompt
      args={args}
      pending={{
        toolCallId,
        step: parsed.step,
        targetUrl: parsed.targetUrl,
        search: parsed.search,
        connectorId,
      }}
      onResult={onResult as ResultHandler}
    />
  );
}

const connectTool = (name: string, args: ConnectToAppArgs): ChatTool => ({
  ...connectToAppActivity(name),
  data: args,
  PendingPrompt: ConnectToAppPendingPrompt,
});

export const connectStepToApp = (args: {
  branchId: string;
  spaceId?: string;
}): ChatTool => connectTool("connectStepToApp", args);

export const connectChatToApp = (args: ConnectToAppArgs): ChatTool =>
  connectTool("connectChatToApp", args);

// Retired name; stored chats still hold entries under it.
export const connectToApp = (args: ConnectToAppArgs): ChatTool =>
  connectTool("connectToApp", args);
